import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, Loader2, ExternalLink, Settings, ShieldAlert, LogOut, WifiOff, Scale, Calendar } from 'lucide-react';
import { GoogleGenAI } from "@google/genai";
import { products } from '../data';

const AIAssistant: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const initialMessage = { 
    role: 'model' as const, 
    text: '👶 **Olá! Sou o FraldaBot, seu especialista em fraldas!**\n\nMe diga:\n• **Peso do bebê** (ex: 8 kg)\n• **Idade** (ex: 1 ano)\n• Ou **necessidade** (noite, pele sensível, econômico)\n\nEu recomendo o **tamanho ideal + link direto da Amazon!**' 
  };
  const [messages, setMessages] = useState<{ role: 'user' | 'model'; text: string }[]>([initialMessage]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [needsKey, setNeedsKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'checking'>('checking');
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // ✅ SUA CHAVE API JÁ ESTÁ AQUI
  const API_KEY = "AIzaSyBHjelP_bhvnI_xDLapqy8hjZAOziFgN0I";

  // 🧠 FUNÇÃO INTELIGENTE: ANALISA PERGUNTA DO USUÁRIO
  const analisarPergunta = (texto: string) => {
    const textoLower = texto.toLowerCase();
    
    // Extrair peso (ex: "8 kg", "5kg", "8kg")
    const pesoMatch = textoLower.match(/(\d+)\s*(kg|kilos?|quilos?)/);
    const peso = pesoMatch ? parseInt(pesoMatch[1]) : null;
    
    // Extrair idade (ex: "5 anos", "8 meses", "2 anos")
    const idadeMatch = textoLower.match(/(\d+)\s*(anos?|meses?|mêses?)/);
    const idade = idadeMatch ? parseInt(idadeMatch[1]) : null;
    
    // Converter idade em meses para comparação
    let idadeEmMeses = null;
    if (idade && idadeMatch) {
      if (idadeMatch[2].includes('ano')) idadeEmMeses = idade * 12;
      else if (idadeMatch[2].includes('mes')) idadeEmMeses = idade;
    }
    
    // Extrair necessidades específicas
    const necessidade = {
      noturna: /noite|dormir|nocturn|madrugada/i.test(texto),
      peleSensivel: /pele sensível|alergia|dermatite|sensíve/i.test(texto),
      economico: /econ[oô]m|barat|pre[çc]o|custo/i.test(texto),
      facilTroca: /fácil|rápido|calcinha|pants|troca/i.test(texto),
      absorcaoMaxima: /absor[çc]|vaz|prote[çc]/i.test(texto),
      recemNascido: /recém|nascido|primeiro|início/i.test(texto)
    };
    
    return { peso, idade, idadeEmMeses, necessidade, textoLower };
  };

  // 📏 FUNÇÃO: CONVERTE PESO PARA TAMANHO DE FRALDA
  const pesoParaTamanho = (peso: number): string => {
    if (peso <= 4) return 'RN';
    if (peso <= 6) return 'P';
    if (peso <= 9) return 'M';
    if (peso <= 13) return 'G';
    if (peso <= 18) return 'XG';
    return 'XXG';
  };

  // 🍼 FUNÇÃO: ENCONTRA FRALDAS POR TAMANHO E NECESSIDADE
  const encontrarFraldasRecomendadas = (analise: ReturnType<typeof analisarPergunta>) => {
    let tamanhoAlvo = '';
    
    // 1. Determinar tamanho pelo peso (prioridade)
    if (analise.peso) {
      tamanhoAlvo = pesoParaTamanho(analise.peso);
    }
    // 2. Se não tem peso, tentar pela idade
    else if (analise.idadeEmMeses) {
      if (analise.idadeEmMeses <= 1) tamanhoAlvo = 'RN';
      else if (analise.idadeEmMeses <= 3) tamanhoAlvo = 'P';
      else if (analise.idadeEmMeses <= 8) tamanhoAlvo = 'M';
      else if (analise.idadeEmMeses <= 18) tamanhoAlvo = 'G';
      else if (analise.idadeEmMeses <= 36) tamanhoAlvo = 'XG';
      else tamanhoAlvo = 'XXG';
    }
    
    // 3. Filtrar produtos
    let produtosFiltrados = products;
    
    // Filtrar por tamanho se identificado
    if (tamanhoAlvo) {
      produtosFiltrados = produtosFiltrados.filter(p => p.size === tamanhoAlvo);
    }
    
    // Filtrar por necessidade
    if (analise.necessidade.peleSensivel) {
      produtosFiltrados = produtosFiltrados.filter(p => 
        p.brand === 'Pampers' && p.name.includes('Confort')
      );
    }
    if (analise.necessidade.noturna) {
      produtosFiltrados = produtosFiltrados.filter(p => 
        p.brand === 'Huggies' || p.name.includes('Supreme') || p.name.includes('Care')
      );
    }
    if (analise.necessidade.economico) {
      produtosFiltrados = produtosFiltrados.sort((a, b) => (a.price || 0) - (b.price || 0));
    }
    if (analise.necessidade.facilTroca) {
      produtosFiltrados = produtosFiltrados.filter(p => 
        p.name.includes('Pants') || p.name.includes('Calcinha')
      );
    }
    if (analise.necessidade.recemNascido) {
      produtosFiltrados = produtosFiltrados.filter(p => 
        p.size === 'RN' || p.ageRange.includes('mês')
      );
    }
    
    // Retornar até 3 produtos
    return produtosFiltrados.slice(0, 3);
  };

  // 🔗 FUNÇÃO PARA GARANTIR LINKS DE AFILIADO
  const processarRespostaComLinks = (texto: string, produtosRecomendados: any[]) => {
    let resultado = texto;
    
    // Adicionar produtos recomendados com links
    if (produtosRecomendados.length > 0) {
      let produtosTexto = '\n\n🛒 **RECOMENDAÇÕES:**\n';
      produtosRecomendados.forEach((prod, idx) => {
        produtosTexto += `\n${idx + 1}. **${prod.brand} ${prod.name}**\n`;
        produtosTexto += `   👶 ${prod.size} | ${prod.weightRange} | ${prod.ageRange}\n`;
        produtosTexto += `   📦 ${prod.units} unidades | R$ ${prod.price?.toFixed(2) || '--'}\n`;
        produtosTexto += `   🔗 ${prod.affiliateUrl}\n`;
      });
      resultado += produtosTexto;
    }
    
    // Garantir que tem pelo menos um link
    if (!resultado.includes('amzn.to') && produtosRecomendados.length === 0) {
      const produtoPadrao = products.find(p => p.affiliateUrl);
      if (produtoPadrao) {
        resultado += `\n\n🔗 **Encontrei esta oferta:** ${produtoPadrao.affiliateUrl}`;
      }
    }
    
    return resultado;
  };

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      
      // Verifica conexão
      if (API_KEY && API_KEY.startsWith('AIza')) {
        setConnectionStatus('connected');
        setNeedsKey(false);
      } else {
        setConnectionStatus('disconnected');
        setNeedsKey(true);
      }
    }
  }, [messages, isOpen]);

  const handleOpenKeyPicker = () => {
    alert("✅ API configurada corretamente!\n\nChave: " + API_KEY.substring(0, 15) + "...");
  };

  const handleCloseChat = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };

  const renderFormattedText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*|https?:\/\/[^\s]+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-bold text-store-purple dark:text-store-accent">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('http')) {
        const isAffiliateLink = part.includes('amzn.to');
        return (
          <a 
            key={i} 
            href={part} 
            target="_blank" 
            rel="noopener noreferrer nofollow"
            className={`inline-flex items-center gap-2 ${isAffiliateLink ? 'bg-store-accent text-store-dark' : 'bg-blue-600 text-white'} px-4 py-3 rounded-xl font-bold text-[10px] uppercase tracking-tight my-2 hover:opacity-90 transition-all shadow-md active:scale-95 border border-white/20 break-all w-full justify-center text-center`}
          >
            <ExternalLink size={11} className="shrink-0" /> 
            {isAffiliateLink ? 'VER OFERTA NA AMAZON' : 'VER PRODUTO'}
          </a>
        );
      }
      return part;
    });
  };

  const handleSend = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || isLoading) return;

    const userMessage = trimmedInput;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setIsLoading(true);

    try {
      // 🧠 ANALISAR PERGUNTA DO USUÁRIO
      const analise = analisarPergunta(userMessage);
      const produtosRecomendados = encontrarFraldasRecomendadas(analise);
      
      // 📝 CRIAR CONTEXTO INTELIGENTE PARA A IA
      const contextoAnalise = [];
      if (analise.peso) contextoAnalise.push(`Peso: ${analise.peso} kg → Tamanho: ${pesoParaTamanho(analise.peso)}`);
      if (analise.idade) contextoAnalise.push(`Idade: ${analise.idade} ${analise.idadeEmMeses && analise.idadeEmMeses > 12 ? 'anos' : 'meses'}`);
      if (analise.necessidade.noturna) contextoAnalise.push('Necessidade: Noite (absorção máxima)');
      if (analise.necessidade.peleSensivel) contextoAnalise.push('Necessidade: Pele sensível');
      if (analise.necessidade.economico) contextoAnalise.push('Necessidade: Econômico');
      
      // ✅ Usar a chave API
      const ai = new GoogleGenAI({ apiKey: API_KEY });
      
      const chat = ai.chats.create({
        model: 'gemini-3-flash-preview',
        config: {
          systemInstruction: `Você é o "FraldaBot", especialista em fraldas.
SUA MISSÃO: Responder sobre fraldas usando SEMPRE links de afiliado (amzn.to).

ANÁLISE DO USUÁRIO: ${contextoAnalise.join(', ') || 'Não especificou peso/idade'}

PRODUTOS RECOMENDADOS (com links):
${produtosRecomendados.map(p => `- ${p.brand} ${p.name} (${p.size}, ${p.units}un) → ${p.affiliateUrl}`).join('\n')}

REGRAS:
1. SEMPRE inclua pelo menos UM link de afiliado
2. Use formato claro: "Recomendo [Produto] - [Link]"
3. Explique PORQUE essa fralda é ideal
4. Se não souber, use os produtos listados acima`,
          temperature: 0.2,
          maxOutputTokens: 600,
        }
      });

      const response = await chat.sendMessage({ 
        message: `Usuário pergunta: "${userMessage}". 
        ${produtosRecomendados.length > 0 ? `Recomende entre esses: ${produtosRecomendados.map(p => p.brand + ' ' + p.name).join(', ')}` : 'Recomende fraldas apropriadas.'}
        INCLUA LINKS DE AFILIADO (amzn.to) em TODAS as recomendações.` 
      });
      
      let aiText = response.text || "Analisando suas necessidades...";
      
      // 🔗 PROCESSAR RESPOSTA COM LINKS
      aiText = processarRespostaComLinks(aiText, produtosRecomendados);
      
      // Adicionar análise inteligente no início
      if (analise.peso || analise.idade) {
        let analiseText = '🔍 **ANÁLISE INTELIGENTE:**\n';
        if (analise.peso) analiseText += `• Peso: ${analise.peso} kg → Tamanho ideal: **${pesoParaTamanho(analise.peso)}**\n`;
        if (analise.idade) analiseText += `• Idade: ${analise.idade} → Recomendação: **${analise.idadeEmMeses && analise.idadeEmMeses <= 12 ? 'Fraldas para bebês' : 'Fraldas calcinha'}**\n`;
        aiText = analiseText + '\n' + aiText;
      }

      setMessages(prev => [...prev, { role: 'model', text: aiText }]);
      
    } catch (error: any) {
      console.error("Erro FraldaBot:", error);
      
      // Fallback: resposta inteligente sem IA
      const analise = analisarPergunta(userMessage);
      const produtosRecomendados = encontrarFraldasRecomendadas(analise);
      
      let fallbackText = '🔍 **ANÁLISE INTELIGENTE:**\n';
      if (analise.peso) fallbackText += `• Peso: ${analise.peso} kg → Tamanho: **${pesoParaTamanho(analise.peso)}**\n`;
      if (analise.idade) fallbackText += `• Idade: ${analise.idade} → Fase: **${analise.idadeEmMeses && analise.idadeEmMeses <= 12 ? 'Bebê' : 'Criança'}**\n`;
      
      fallbackText += '\n🛒 **RECOMENDAÇÕES DIRETAS:**\n';
      produtosRecomendados.forEach((prod, idx) => {
        fallbackText += `\n${idx + 1}. **${prod.brand} ${prod.name}** (${prod.size})\n`;
        fallbackText += `   🔗 ${prod.affiliateUrl}\n`;
      });
      
      if (produtosRecomendados.length === 0) {
        const produtoPadrao = products.find(p => p.affiliateUrl);
        if (produtoPadrao) {
          fallbackText += `\n🔗 **Oferta disponível:** ${produtoPadrao.affiliateUrl}`;
        }
      }
      
      setMessages(prev => [...prev, { role: 'model', text: fallbackText }]);
      
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[100] font-sans flex flex-col items-end">
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-store-purple hover:bg-store-dark text-white p-3 rounded-full shadow-lg transition-all transform hover:scale-105 flex items-center gap-2 group relative border-2 border-white dark:border-gray-800"
        >
          <div className="absolute -top-1 -right-1 bg-store-accent text-store-dark text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-sm border border-white">
            {connectionStatus === 'connected' ? '✅' : '⚠️'}
          </div>
          <Bot size={24} />
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-500 font-semibold whitespace-nowrap text-xs">
            FraldaBot {connectionStatus === 'connected' ? 'Online' : 'Offline'}
          </span>
        </button>
      )}

      {isOpen && (
        <div 
          className="bg-white dark:bg-gray-900 w-[calc(100vw-2rem)] sm:w-[380px] md:w-[400px] h-[500px] max-h-[70vh] sm:max-h-[85vh] rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 flex flex-col overflow-hidden animate-fade-in"
        >
          {/* Header */}
          <div className={`px-5 py-3 flex justify-between items-center text-white shrink-0 border-b border-white/10 ${connectionStatus === 'connected' ? 'bg-store-purple' : 'bg-red-500'}`}>
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-1.5 rounded-xl">
                <Bot size={18} />
              </div>
              <div>
                <h3 className="font-bold text-sm tracking-tight leading-none">FraldaBot Elite</h3>
                <p className="text-[7px] opacity-80 font-semibold uppercase tracking-wider mt-0.5">
                  Status: {connectionStatus === 'connected' ? '✅ CONECTADO' : '🔴 OFFLINE'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={handleOpenKeyPicker}
                className={`p-1.5 rounded-lg transition-all ${needsKey ? 'bg-store-accent text-store-dark animate-pulse shadow' : 'hover:bg-white/20 text-white/70'}`}
                title="Configurar API"
              >
                <Settings size={16} />
              </button>
              <button 
                onClick={handleCloseChat} 
                className="hover:bg-white/10 p-1.5 rounded-full text-white transition-all active:scale-90"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div 
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50/20 dark:bg-gray-950 scroll-smooth min-h-0"
          >
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                ref={idx === messages.length - 1 ? lastMessageRef : null}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] px-4 py-3 rounded-xl text-[12px] leading-relaxed shadow-sm break-words border ${msg.role === 'user' 
                  ? 'bg-store-purple text-white rounded-tr-none border-store-purple/20' 
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-tl-none border-gray-100 dark:border-gray-700'}`}>
                  <div className="whitespace-pre-wrap">{renderFormattedText(msg.text)}</div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-800 px-3 py-2 rounded-xl rounded-tl-none border border-gray-100 dark:border-gray-700 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-store-purple" />
                  <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide italic">Analisando peso e idade...</span>
                </div>
              </div>
            )}
            <div className="h-3 shrink-0" />
          </div>

          {/* Input Area */}
          <div className="px-4 py-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 shrink-0">
            {needsKey ? (
              <div className="space-y-3">
                 <div className="flex items-center gap-2 text-red-500 justify-center bg-red-50 dark:bg-red-900/20 py-2 rounded-xl border border-red-100 dark:border-red-900/30">
                    <WifiOff size={16} />
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-tight">CHAVE API NÃO CONFIGURADA</p>
                        <p className="text-[8px] opacity-70 leading-none">Verifique o código</p>
                    </div>
                 </div>
                 <button 
                  onClick={handleOpenKeyPicker}
                  className="w-full bg-store-accent text-store-dark font-bold text-xs py-3 rounded-xl shadow hover:bg-yellow-300 transition-all flex items-center justify-center gap-2"
                >
                  <Settings size={14} /> CONFIGURAR CHAVE API
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Ex: Bebê de 8 kg, pele sensível..."
                    className="flex-1 bg-gray-100 dark:bg-gray-800 border-none rounded-lg px-3 py-2 text-xs outline-none text-gray-800 dark:text-white placeholder:text-gray-400 focus:ring-1 focus:ring-store-purple/30 transition-all"
                  />
                  <button
                    onClick={handleSend}
                    disabled={isLoading || !input.trim()}
                    className="bg-store-purple hover:bg-store-dark text-white p-2 rounded-lg disabled:opacity-50 transition-all shadow active:scale-95 flex items-center justify-center shrink-0"
                  >
                    <Send size={16} />
                  </button>
                </div>

                <button 
                  onClick={() => { if(window.confirm("Encerrar atendimento?")) { setMessages([initialMessage]); setIsOpen(false); } }}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-bold text-[10px] py-2 rounded-lg uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow active:scale-95"
                >
                  <LogOut size={11} /> Encerrar Consultoria
                </button>
              </div>
            )}
            
            <div className="mt-2 flex items-center justify-center gap-1 opacity-50">
                <ShieldAlert size={9} className="text-store-accent" />
                <p className="text-[7px] font-semibold uppercase tracking-wider italic">Segurança FraldaTech</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;